import React from 'react'
import { DateTime } from 'luxon'
import { useMediaQuery } from 'react-responsive'
import { Notification } from '~/models'
import { notificationsStore } from '~/stores'
import { memo } from '~/ui/component'
import { Chip, HBox, Label, ListItem, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import { screenMinWidths } from '~/ui/styling/layout'
import { Avatar } from '../media'
import { imageSize, isNotificationAvatar } from './NotificationPanel'

export interface Props {
  notification: Notification
}

const NotificationListItem = memo('NotificationListItem', (props: Props) => {

  const {notification} = props

  const isMobile = !useMediaQuery({minWidth: screenMinWidths.tablet})

  const markAsSeen = React.useCallback(
    () => notificationsStore.markAsSeen(notification.id),
    [notification.id],
  )

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <ListItem
        image={renderAvatar()}
        caption={notification.title ?? notificationsStore.defaultTitle}
        detail={renderDetail()}
        href={notification.url}
        classNames={[$.NotificationListItem, {unseen: !notification.seen}]}
        onTap={markAsSeen}
        spacious
      />
    )
  }

  function renderDetail() {
    const chips = (notification.labels?.map(label => (
      <Chip key={label} flex='none' small dim>{label}</Chip>
    )) ?? [])
    const dimBody = notification.seen || chips.length > 0

    // On mobile, the labels get their own line between the title and the body.
    if (isMobile && chips.length > 0) {
      return (
        <VBox gap={layout.padding.inline.xs}>
          <HBox gap={layout.padding.inline.s} wrap>
            {chips}
          </HBox>
          {renderBodyRow([], dimBody)}
        </VBox>
      )
    }

    return renderBodyRow(chips, dimBody)
  }

  function renderBodyRow(chips: React.ReactNode[], dimBody: boolean) {
    return (
      <HBox classNames={$.bodyRow} gap={layout.padding.inline.s}>
        <HBox classNames={$.body} gap={layout.padding.inline.s}>
          {chips}
          <Label
            flex='shrink'
            small
            dim={dimBody}
            markup
            convertNewlines={false}
            children={notification.body}
          />
        </HBox>
        <Label classNames={$.createdAt} small dim align='right' truncate={false}>
          {renderCreatedAt()}
        </Label>
      </HBox>
    )
  }

  function renderCreatedAt() {
    const minutes = Math.max(0, Math.floor(DateTime.now().diff(notification.createdAt, 'minutes').minutes))
    if (minutes >= 24 * 60) {
      return `${Math.floor(minutes / (24 * 60))}d`
    }
    if (minutes >= 60) {
      return `${Math.floor(minutes / 60)}u`
    }
    return `${minutes}m`
  }

  function renderAvatar() {
    if (!isNotificationAvatar(notification.avatar)) {
      return notification.avatar
    }

    return (
      <Avatar
        source={notification.avatar.photoURL}
        firstName={notification.avatar.firstName}
        lastName={notification.avatar.lastName}
        size={imageSize}
      />
    )
  }

  return render()

})

export default NotificationListItem

const useStyles = createUseStyles(theme => ({
  NotificationListItem: {
    '&.unseen': {
      backgroundColor: theme.semantic.primary.alpha(0.15),
    },
  },

  bodyRow: {
    minWidth: 0,
  },

  body: {
    flex:     [1, 1, '90%'],
    minWidth: 0,
  },

  createdAt: {
    flex:     [0, 0, '10%'],
    minWidth: 'max-content',
  },
}))